import { useQuery } from '@apollo/client';
import { GetUsersByChannelIdQuery, User } from 'graphql-query-contracts';
import { SortDirectionEnum } from 'constants-activate';

import { getUsersByChannelId } from 'lane-shared/graphql/query';

import { DateTime } from 'luxon';

import { UserLoginType } from 'packages/lane-shared/types/UserLogin';

export type AllUsersRes = {
  _id: string;
  _created: string | DateTime | Date | null | undefined;
  status: string;
  lastSeen: string;
  logins: UserLoginType[];
  name: string;
  person?: {
    units: {
      name: string;
      floors?: { name: string }[];
    }[];
    resident: { status: string; type: string };
  };
  company: {
    _id: string;
    name: string;
  }[];
  roles: {
    _id: string;
    groupRole: {
      name: string;
    };
    relatedChannels: {
      _id: string;
      name: string;
    }[];
  };
};

export type AllUsersSearch = {
  groupRoles: string;
  company: string;
  companyRoles: string;
  keyword: string;
  userStatus: string;
  sortBy: string;
  sortDirection: SortDirectionEnum;
};

export const useAllUsers = (
  channelId: string,
  _includeResidentInfo: boolean = false,
  {
    groupRoles: groupRolesSearch,
    company,
    companyRoles: companyRolesSearch,
    keyword,
    userStatus,
    sortBy,
    sortDirection,
  }: AllUsersSearch,
  pagination: { page: string; pageSize: string }
) => {
  // Normalize inputs so Apollo's deep-equality check on variables treats the
  // mount render and the immediate `useQueryString` URL sync as identical.
  // The URL sync swaps typed defaults (number, null) for parsed query-string
  // values (string, empty string), which would otherwise change variables on
  // the second render and cancel the in-flight `getUsersByChannelId` request.
  const page = Number(pagination.page);
  const pageSize = Number(pagination.pageSize);
  const keywordValue = keyword ?? '';

  const {
    data,
    loading: userLoading,
    refetch,
  } = useQuery<GetUsersByChannelIdQuery>(
    // Resident info query currently not in use
    // includeResidentInfo
    //   ? queryChannelUsersByGroupRolesIncludeResidentInfo
    //   : queryChannelUsersByGroupRoles,
    getUsersByChannelId,
    {
      variables: {
        search: {
          groupRoleIds:
            groupRolesSearch?.length > 0 ? groupRolesSearch.split(',') : [],
          company: company?.length > 0 ? company.split(',') : [],
          companyRoleTemplateIds:
            companyRolesSearch?.length > 0
              ? companyRolesSearch.split(',')
              : undefined,
          search: keywordValue,
          status: userStatus?.length > 0 ? userStatus.split(',') : undefined,
          ...(sortBy
            ? {
                sortBy: {
                  key: sortBy,
                  dir: sortDirection,
                },
              }
            : {}),
        },
        channelId,
        pagination: {
          start: page * pageSize,
          perPage: pageSize,
        },
      },
      fetchPolicy: 'cache-and-network',
    }
  );
  const { items, pageInfo } = data?.usersByChannelId ?? {};

  const loading = userLoading;

  return {
    users: (items ?? []) as User[],
    total: pageInfo?.total ?? 0,
    loading,
    refetch,
  };
};
